import React, { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
} from '@abgov/react-components';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import { updateCollision } from '../../state/slices/collisionSlice';

import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';

import { PassengerCardComponent } from './passenger-card';
import { Collision, Passenger } from '../../models/collision';
import { NotificationType } from '../notifications/notification-component';
import { isValidPhone } from '../../utils/formUtils';

interface PassengerInformationProps extends BaseWizardProps {}

export const PassengerInformationComponent = ({
  navigationConfiguration,
}: PassengerInformationProps) => {
  const maxPassengers = 8;
  const locationInVehicleOtherSpecifyValue = 82;
  const safetyEquipmentUsedOtherSpecifyValue = 92;

  const selectionErrorText = 'Make a selection below';
  const passengerCountSelectionErrorText =
    'Select the number of passengers involved';

  const dispatch = useDispatch<AppDispatch>();
  const stateCollision = useSelector((state: RootState) => state.collision);

  const [passengerCount, setPassengerCount] = useState(
    stateCollision?.collision?.vehicles?.[0]?.numberOfPassengers ?? null,
  );
  const [passengerList, setPassengerList] = useState<Passenger[]>(
    stateCollision?.collision?.vehicles?.[0]?.passengers || [],
  );
  const [passengerCards, setPassengerCards] = useState<JSX.Element[]>([]);
  const [firstNameErrorText, setFirstNameErrorText] = useState<string>('');
  const [lastNameErrorText, setLastNameErrorText] = useState<string>('');
  const [dateOfBirthErrorText, setDateOfBirthErrorText] = useState<string>('');
  const [postalCodeOrZipErrorMessage, setPostalCodeErrorMessage] =
    useState<string>('');
  const [homePhoneErrorMessage, setHomePhoneErrorMessage] =
    useState<string>('');
  const [workPhoneErrorMessage, setWorkPhoneErrorMessage] =
    useState<string>('');

  const unknownGenderId = '3';

  const [passengerErrors, setPassengerErrors] = useState<
    {
      locationInVehicleError: boolean;
      safetyEquipmentUsedError: boolean;
      firstNameError: boolean;
      lastNameError: boolean;
      dateOfBirthError: boolean;
      postalCodeError: boolean;
      primaryPhoneError: boolean;
      alternatePhoneError: boolean;
    }[]
  >([]);

  const [passengerCountSelectionError, setPassengerCountSelectionError] =
    useState('');
  const [nextPage, setNextPage] = useState('');

  const handleNumberOfPassengersChange = (value: string) => {
    const count = parseInt(value);
    setPassengerCount(count);
    dispatch(removeAllValidationErrorNotifications());
  };

  const validate = (): boolean => {
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

    let isValid = true;
    let isSelectionError = false;

    if (passengerCount === null) {
      setPassengerCountSelectionError(passengerCountSelectionErrorText);
      isSelectionError = true;
      isValid = false;
    }

    const updatedErrors = passengerList.map((passenger, index) => {
      let passengerError = {
        locationInVehicleError: false,
        safetyEquipmentUsedError: false,
        firstNameError: false,
        lastNameError: false,
        dateOfBirthError: false,
        postalCodeError: false,
        primaryPhoneError: false,
        alternatePhoneError: false,
      };

      if (!passengerList[index].positionInVehicle) {
        passengerError.locationInVehicleError = true;
        isSelectionError = true;
        isValid = false;
      } else {
        passengerError.locationInVehicleError = false;
      }

      if (!passengerList[index].safetyEquipmentUsed) {
        passengerError.safetyEquipmentUsedError = true;
        isSelectionError = true;
        isValid = false;
      } else {
        passengerError.safetyEquipmentUsedError = false;
      }

      if (passengerList[index].dateOfBirth) {
        const dateOfBirth = new Date(passengerList[index].dateOfBirth);
        const currentDate = new Date();
        if (dateOfBirth >= currentDate) {
          passengerError.dateOfBirthError = true;
          setDateOfBirthErrorText('Invalid date of birth');
          isValid = false;
        }
      }

      if (
        passengerList[index].address?.postalCode &&
        !postalCodeRegex.test(passengerList[index].address?.postalCode)
      ) {
        passengerError.postalCodeError = true;
        setPostalCodeErrorMessage('Invalid postal code');
        isValid = false;
      }

      if (
        passengerList[index].homePhoneNumber &&
        !isValidPhone(passengerList[index].homePhoneNumber)
      ) {
        passengerError.primaryPhoneError = true;
        setHomePhoneErrorMessage('Invalid phone number');
        isValid = false;
      }

      if (
        passengerList[index].workPhoneNumber &&
        !isValidPhone(passengerList[index].workPhoneNumber)
      ) {
        passengerError.alternatePhoneError = true;
        setWorkPhoneErrorMessage('Invalid phone number');
        isValid = false;
      }

      return passengerError;
    });

    setPassengerErrors(updatedErrors);

    if (isSelectionError) {
      dispatch(
        pushNotification({
          body: selectionErrorText,
          notificationType: NotificationType.importantValidationError,
        }),
      );
    }

    if (isValid) {
      if (passengerList.length) {
        setPassengerErrors(
          Array.from({ length: passengerList.length }, () => ({
            locationInVehicleError: false,
            safetyEquipmentUsedError: false,
            firstNameError: false,
            lastNameError: false,
            dateOfBirthError: false,
            postalCodeError: false,
            primaryPhoneError: false,
            alternatePhoneError: false,
          })),
        );
      }
    }

    return isValid;
  };

  const generatePassenger = (id: number): Passenger => ({
    id: id,
    firstName: '',
    lastName: '',
    gender: '0',
    dateOfBirth: undefined,
    address: {
      address: '',
      city: '',
      province: '',
      postalCode: '',
      country: '',
    },
    homePhoneNumber: '',
    workPhoneNumber: '',
    positionInVehicle: null,
    positionInVehicleDescription: '',
    safetyEquipmentUsed: null,
    safetyEquipmentUsedDescription: '',
  });

  const updatePassengerListBasedOnPassengerCountChange = (
    newPassengerCount: number | null,
  ) => {
    setPassengerList((prevPassengerList) => {
      if (!newPassengerCount) {
        return [];
      }

      const currentCount = prevPassengerList.length;
      if (newPassengerCount > currentCount) {
        const passengersToAdd = newPassengerCount - currentCount;
        const newPassengers = Array.from({ length: passengersToAdd }, (_, i) =>
          generatePassenger(currentCount + i + 1),
        );
        return [...prevPassengerList, ...newPassengers];
      } else if (newPassengerCount < currentCount) {
        // Remove passengers from the end
        return prevPassengerList.slice(0, newPassengerCount);
      } else {
        return prevPassengerList;
      }
    });

    setPassengerErrors(
      Array.from({ length: newPassengerCount || 0 }, () => ({
        locationInVehicleError: false,
        safetyEquipmentUsedError: false,
        firstNameError: false,
        lastNameError: false,
        dateOfBirthError: false,
        postalCodeError: false,
        primaryPhoneError: false,
        alternatePhoneError: false,
      })),
    );
  };

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  useEffect(() => {
    console.log('passenger count:', passengerCount);
    updatePassengerListBasedOnPassengerCountChange(passengerCount);
  }, [passengerCount]);

  useEffect(() => {
    if (!passengerCount) {
      setPassengerCards([]);
      return;
    }
    // console.log("Loading passengerList:", passengerList);
    const cardList: JSX.Element[] = passengerList.map((passenger, index) => (
      <div key={passenger.id}>
        <PassengerCardComponent
          id={passenger.id}
          setId={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                id: value,
              };
              return updatedPassengers;
            });
          }}
          firstName={passenger.firstName}
          setFirstName={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                firstName: value,
              };
              return updatedPassengers;
            });
          }}
          lastName={passenger.lastName}
          setLastName={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                lastName: value,
              };
              return updatedPassengers;
            });
          }}
          sex={passenger.gender || ''}
          setSex={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                gender: value,
              };
              return updatedPassengers;
            });
          }}
          dateOfBirth={passenger.dateOfBirth || ''}
          setDateOfBirth={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                dateOfBirth: value,
              };
              return updatedPassengers;
            });
          }}
          address={passenger.address?.address || ''}
          setAddress={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                address: {
                  ...updatedPassengers[index].address,
                  address: value,
                },
              };
              return updatedPassengers;
            });
          }}
          city={passenger.address?.city || ''}
          setCity={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index].address = {
                ...updatedPassengers[index].address,
                city: value,
              };
              return updatedPassengers;
            });
          }}
          country={passenger.address?.country || ''}
          setCountry={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                address: {
                  ...updatedPassengers[index].address,
                  country: value,
                },
              };
              return updatedPassengers;
            });
          }}
          postalCode={passenger.address?.postalCode || ''}
          setPostalCode={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                address: {
                  ...updatedPassengers[index].address,
                  postalCode: value,
                },
              };
              return updatedPassengers;
            });
          }}
          province={passenger.address?.province || ''}
          setProvince={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                address: {
                  ...updatedPassengers[index].address,
                  province: value,
                },
              };
              return updatedPassengers;
            });
          }}
          primaryPhone={passenger.homePhoneNumber || ''}
          setPrimaryPhone={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                homePhoneNumber: value,
              };
              return updatedPassengers;
            });
          }}
          alternatePhone={passenger.workPhoneNumber || ''}
          setAlternatePhone={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                workPhoneNumber: value,
              };
              return updatedPassengers;
            });
          }}
          locationInVehicle={
            passenger.positionInVehicle !== null
              ? passenger.positionInVehicle.toString()
              : ''
          }
          setLocationInVehicle={(value) => {
            console.log(
              `Updating passenger ${index} positionInVehicle to:`,
              value,
            );
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                positionInVehicle: value !== '' ? parseInt(value, 10) : null,
              };
              return updatedPassengers;
            });
          }}
          showSpecifyLocation={
            passenger.positionInVehicle === locationInVehicleOtherSpecifyValue
          }
          setShowSpecifyLocation={(value) => {}}
          specifyLocation={passenger.positionInVehicleDescription || ''}
          setSpecifyLocation={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                positionInVehicleDescription: value,
              };
              return updatedPassengers;
            });
          }}
          showSpecifySafetyEquipmentUsed={
            passenger.safetyEquipmentUsed ===
            safetyEquipmentUsedOtherSpecifyValue
          }
          setShowSpecifySafetyEquipmentUsed={(value) => {}}
          safetyEquipmentUsed={
            passenger.safetyEquipmentUsed !== null
              ? passenger.safetyEquipmentUsed.toString()
              : ''
          }
          setSafetyEquipmentUsed={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                safetyEquipmentUsed: value !== '' ? parseInt(value, 10) : null,
              };
              return updatedPassengers;
            });
          }}
          specifySafetyEquipmentUsed={
            passenger.safetyEquipmentUsedDescription || ''
          }
          setSpecifySafetyEquipmentUsed={(value) => {
            setPassengerList((prevPassengerList) => {
              const updatedPassengers = [...prevPassengerList];
              updatedPassengers[index] = {
                ...updatedPassengers[index],
                safetyEquipmentUsedDescription: value,
              };
              return updatedPassengers;
            });
          }}
          // Error handling
          locationInVehicleError={
            passengerErrors[index]?.locationInVehicleError || false
          }
          setLocationInVehicleError={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                locationInVehicleError: value,
              };
              return updatedErrors;
            });
          }}
          // locationInVehicleError={passengerErrors[index]?.locationInVehicleError || false}
          // setLocationInVehicleError={(value) => {
          //     const updatedErrors = [...passengerErrors];
          //     updatedErrors[index] = {
          //         ...updatedErrors[index],
          //         locationInVehicleError: value,
          //     };
          //     setPassengerErrors(updatedErrors);
          // }}

          safetyEquipmentUsedError={
            passengerErrors[index]?.safetyEquipmentUsedError || false
          }
          setSafetyEquipmentUsedError={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                safetyEquipmentUsedError: value,
              };
              return updatedErrors;
            });
          }}
          firstNameErrorMessage={
            passengerErrors[index]?.firstNameError ? firstNameErrorText : ''
          }
          setFirstNameErrorMessage={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                firstNameError: value.length > 0,
              };
              return updatedErrors;
            });
          }}
          lastNameErrorMessage={
            passengerErrors[index]?.lastNameError ? lastNameErrorText : ''
          }
          setLastNameErrorMessage={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                lastNameError: value.length > 0,
              };
              return updatedErrors;
            });
          }}
          dateOfBirthErrorMessage={
            passengerErrors[index]?.dateOfBirthError ? dateOfBirthErrorText : ''
          }
          setDateOfBirthErrorMessage={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                dateOfBirthError: value.length > 0,
              };
              return updatedErrors;
            });
          }}
          postalCodeErrorMessage={
            passengerErrors[index]?.postalCodeError
              ? postalCodeOrZipErrorMessage
              : ''
          }
          setPostalCodeErrorMessage={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                postalCodeError: value.length > 0,
              };
              return updatedErrors;
            });
          }}
          primaryPhoneErrorMessage={
            passengerErrors[index]?.primaryPhoneError
              ? homePhoneErrorMessage
              : ''
          }
          setPrimaryPhoneErrorMessage={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                primaryPhoneError: value.length > 0,
              };
              return updatedErrors;
            });
          }}
          alternatePhoneErrorMessage={
            passengerErrors[index]?.alternatePhoneError
              ? workPhoneErrorMessage
              : ''
          }
          setAlternatePhoneErrorMessage={(value) => {
            setPassengerErrors((prevErrors) => {
              const updatedErrors = [...prevErrors];
              updatedErrors[index] = {
                ...updatedErrors[index],
                alternatePhoneError: value.length > 0,
              };
              return updatedErrors;
            });
          }}
        />
      </div>
    ));

    // console.log("cardList:", cardList.length);
    setPassengerCards(cardList);
    // console.log("passengerList:", passengerList);
  }, [passengerList, passengerErrors]);

  const save = () => {
    let updatedCollision: Collision = { ...stateCollision.collision };

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    updatedCollision.vehicles = [
      {
        ...stateCollision?.collision?.vehicles![0],
        numberOfPassengers: passengerCount ?? null,
        passengers: passengerList,
      },
    ];

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
        }}
      >
        <h1>Passenger information</h1>
        <p>
          Please provide the required information below, excluding yourself. If
          other passengers were involved in the collision, you'll be prompted to
          provide additional details.
        </p>
        <div>
          <GoAFormItem
            label="How many passengers were in the vehicle?"
            labelSize="regular"
            error={passengerCountSelectionError}
          >
            <GoADropdown
              name="passengerCountSelection"
              value={passengerCount !== null ? passengerCount.toString() : ''}
              width="343px"
              error={passengerCountSelectionError.length > 0}
              relative={true}
              onChange={(_, value) => {
                if (value) {
                  handleNumberOfPassengersChange(value.toString());
                }
                setPassengerCountSelectionError('');
              }}
              placeholder="—Select—"
            >
              {Array.from({ length: maxPassengers + 1 }, (_, i) => (
                <GoADropdownItem
                  key={i}
                  value={i.toString()}
                  label={i.toString()}
                ></GoADropdownItem>
              ))}
            </GoADropdown>
          </GoAFormItem>
        </div>
        <div>{passengerCards}</div>
      </div>
      <WizardButtonGroup
        nextPage={nextPage}
        validate={(): boolean => {
          return validate();
        }}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigationConfiguration.navigate}
      />
    </div>
  );
};
